/**
 * Auth state machine for the bridge.
 *
 * Three terminal states are exposed to hosts:
 *   - `unknown`         — no native handshake has resolved yet
 *   - `authenticated`   — the most recent verifyToken call succeeded
 *   - `unauthenticated` — startup found no session, login failed, or the user
 *                         was logged out (explicitly or via the native side)
 *
 * The store is the single source of truth so hosts no longer have to derive
 * this from `onTokenVerified` + `onLoggedOut`. Pairs naturally with React's
 * `useSyncExternalStore`.
 */

export const AUTH_STATUS = Object.freeze({
    UNKNOWN: 'unknown',
    AUTHENTICATED: 'authenticated',
    UNAUTHENTICATED: 'unauthenticated',
});

/**
 * @typedef {'unknown'|'authenticated'|'unauthenticated'} AuthStatus
 *
 * @typedef {Object} AuthState
 * @property {AuthStatus} status
 * @property {any} [account]      Resolved account from `onTokenVerified` (when authenticated).
 * @property {string} [reason]    Logout reason (when unauthenticated). See LOGOUT_REASON.
 */

export const LOGOUT_REASON = Object.freeze({
    APP_VERSION_TOO_LOW: 'app-version-too-low',
    NO_SESSION: 'no-session',
    VERIFY_FAILED: 'verify-failed',
    LOGIN_FAILED: 'login-failed',
    LOGOUT_EAGER: 'logout-eager',
    LOGOUT_CONFIRMED: 'logout-confirmed',
});

/**
 * Create a tiny observable auth-state store.
 *
 * Listeners are invoked synchronously after every state change. Errors thrown
 * by listeners are caught and routed to `logger.error` so a single misbehaving
 * subscriber cannot break the chain.
 *
 * @param {{ error?: (...a: any[]) => void }} [logger]
 */
export function createAuthStore(logger) {
    /** @type {AuthState} */
    let current = { status: AUTH_STATUS.UNKNOWN };
    /** @type {Set<(s: AuthState) => void>} */
    const listeners = new Set();

    function notify() {
        for (const listener of listeners) {
            try { listener(current); }
            catch (err) { logger && logger.error && logger.error('adxdb-bridge: auth listener threw', err); }
        }
    }

    return {
        /** @returns {AuthState} */
        get() { return current; },

        /**
         * Replace the current state. Listeners only fire when the status
         * actually transitions; identical successive transitions are coalesced
         * (this keeps `bridge.logout()` from notifying twice when the eager
         * call and the native confirmation both land).
         *
         * @param {AuthState} next
         * @returns {boolean} whether listeners were notified
         */
        set(next) {
            const sameStatus = next.status === current.status;
            const sameAccount = next.account === current.account;
            if (sameStatus && sameAccount) {
                // Still update reason metadata silently so the latest cause is
                // queryable via `get()`, but don't wake listeners.
                current = { ...current, ...next };
                return false;
            }
            current = next;
            notify();
            return true;
        },

        /**
         * @param {(s: AuthState) => void} listener
         * @returns {() => void} unsubscribe
         */
        subscribe(listener) {
            listeners.add(listener);
            return () => { listeners.delete(listener); };
        },
    };
}
